import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { gtmNewsletterSubmit } from '../utils/gtm';
import {
  Container,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Paper,
  Button,
  ClickAwayListener,
} from '@mui/material';
import {
  Search as SearchIcon,
  AccessTime,
  Close as CloseIcon,
  CreditScore,
  Receipt,
  Savings,
  Home,
  AccountBalance,
  MenuBook,
  Build,
  CompareArrows,
  EmailOutlined,
} from '@mui/icons-material';
import SEOHead from '../components/SEO/SEOHead';
import BreadcrumbNav from '../components/SEO/BreadcrumbNav';
import {
  financlopediaEntries,
  financlopediaBySlug,
  FEATURED_SLUGS,
  CLUSTERS,
  SMART_SEARCH_EXTRAS,
} from '../data/financlopediaData';
import ImpactBadge from '../components/Financlopedia/ImpactBadge';
import TrustStrip from '../components/Layout/TrustStrip';


/* ── Constants ─────────────────────────────────────────────────────── */

const CATEGORIES = [
  { key: 'todos', label: 'Todos' },
  { key: 'ahorro', label: 'Ahorro' },
  { key: 'credito', label: 'Crédito' },
  { key: 'impuestos', label: 'Impuestos' },
  { key: 'inversion', label: 'Inversión' },
  { key: 'vivienda', label: 'Vivienda' },
  { key: 'vida', label: 'Vida financiera' },
];

const DIFFICULTIES = [
  { key: 'basico', label: 'Básico' },
  { key: 'intermedio', label: 'Intermedio' },
  { key: 'avanzado', label: 'Avanzado' },
];

const difficultyLabel = {
  basico: 'Básico',
  intermedio: 'Intermedio',
  avanzado: 'Avanzado',
};

const difficultyColor = {
  basico: { bg: '#E8F5E9', text: '#2E7D32' },
  intermedio: { bg: '#FFF8E1', text: '#7D5A00' },
  avanzado: { bg: '#FFEBEE', text: '#C62828' },
};

const CLUSTER_ICONS = {
  CreditScore,
  Receipt,
  Savings,
  Home,
  AccountBalance,
};

const SEARCH_TYPE_ICONS = {
  term: SearchIcon,
  guide: MenuBook,
  tool: Build,
  comparator: CompareArrows,
};

const SEARCH_TYPE_LABELS = {
  term: 'Término',
  guide: 'Guía',
  tool: 'Herramienta',
  comparator: 'Comparador',
};

const schema = {
  '@context': 'https://schema.org',
  '@type': 'DefinedTermSet',
  name: 'Diccionario Financiero Canadiense en Español',
  description:
    'Más de 50 términos financieros usados en Canadá explicados para latinos recién llegados.',
  url: 'https://www.finmaple.com/financlopedia',
  hasDefinedTerm: financlopediaEntries.map((e) => ({
    '@type': 'DefinedTerm',
    name: e.term,
    description: e.atomicAnswer,
    url: `https://www.finmaple.com/financlopedia/${e.slug}`,
  })),
};

/* ── Alphabetical helpers ─────────────────────────────────────────── */

const getAlphaGroups = (entries) => {
  const groups = {};
  entries.forEach((entry) => {
    const letter = entry.term.charAt(0).toUpperCase();
    if (!groups[letter]) groups[letter] = [];
    groups[letter].push(entry);
  });
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
};

const sortedEntries = [...financlopediaEntries].sort((a, b) =>
  a.term.localeCompare(b.term)
);

const ALPHA_GROUPS = getAlphaGroups(sortedEntries);
const AVAILABLE_LETTERS = ALPHA_GROUPS.map(([letter]) => letter);

/* ── Entry card ────────────────────────────────────────────────────── */

const EntryCard = ({ entry, featured = false }) => (
  <Box
    component={RouterLink}
    to={`/financlopedia/${entry.slug}`}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      bgcolor: '#FFFFFF',
      border: featured ? '1.5px solid #1E3A5F' : '1px solid #E6E9EE',
      borderRadius: featured ? '18px' : '14px',
      boxShadow: featured ? '0 6px 18px rgba(30,58,95,0.08)' : '0 2px 8px rgba(0,0,0,0.03)',
      p: { xs: '16px', md: featured ? '22px' : '18px' },
      textDecoration: 'none',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        boxShadow: '0 8px 28px rgba(0,0,0,0.07)',
        transform: 'translateY(-3px)',
      },
    }}
  >
    {/* Title */}
    <Typography sx={{ fontSize: featured ? '17px' : '15px', fontWeight: 600, color: '#1A1A1A', mb: 1, lineHeight: 1.3 }}>
      {entry.term}
    </Typography>

    {/* Atomic answer - 2 lines max */}
    <Typography
      sx={{
        fontSize: '13px',
        color: '#5F6B7A',
        lineHeight: 1.6,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        flex: 1,
        mb: 2,
      }}
    >
      {entry.atomicAnswer}
    </Typography>

    {/* Footer: impact first · difficulty · readTime (quietest) */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
      {entry.impact && <ImpactBadge impact={entry.impact} size="small" />}
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 500,
          color: difficultyColor[entry.difficulty]?.text || '#5F6B7A',
        }}
      >
        {difficultyLabel[entry.difficulty] || entry.difficulty}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, opacity: 0.5 }}>
        <AccessTime sx={{ fontSize: 11, color: '#9CA3AF' }} />
        <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
          {entry.readTime}m
        </Typography>
      </Box>
    </Box>
  </Box>
);

/* ── Compact A–Z list item (for alphabetical view) ────────────────── */

const AlphaListItem = ({ entry }) => (
  <Box
    component={RouterLink}
    to={`/financlopedia/${entry.slug}`}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 2, py: 1.5, px: 2, textDecoration: 'none',
      borderBottom: '1px solid #F3F4F6',
      transition: 'background 0.15s ease',
      '&:hover': { bgcolor: '#F7F9FC' },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>
        {entry.term}
        {entry.termFull !== entry.term && (
          <Typography component="span" sx={{ fontSize: '13px', fontWeight: 400, color: '#9CA3AF', ml: 1 }}>
            {entry.termFull}
          </Typography>
        )}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      {entry.impact && <ImpactBadge impact={entry.impact} size="small" />}
      <Typography sx={{ fontSize: '11px', color: difficultyColor[entry.difficulty]?.text || '#5F6B7A', fontWeight: 500 }}>
        {difficultyLabel[entry.difficulty]}
      </Typography>
    </Box>
  </Box>
);

/* ── Cluster section ──────────────────────────────────────────────── */

const ClusterSection = ({ cluster }) => {
  const Icon = CLUSTER_ICONS[cluster.icon] || AccountBalance;
  const entries = cluster.slugs
    .map((slug) => financlopediaBySlug[slug])
    .filter(Boolean);

  if (entries.length === 0) return null;

  return (
    <Box sx={{ mb: 7 }}>
      {/* Cluster header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: '10px',
            bgcolor: `${cluster.color}12`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Icon sx={{ color: cluster.color, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            variant="h5" component="h2"
            sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: { xs: '1.1rem', md: '1.2rem' }, lineHeight: 1.3 }}
          >
            {cluster.name}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.4, mt: 0.25 }}>
            {cluster.description}
          </Typography>
        </Box>
      </Box>

      {/* Cluster entries grid */}
      <Grid container spacing={2.5}>
        {entries.map((entry) => (
          <Grid item xs={12} sm={6} md={4} key={entry.slug}>
            <EntryCard entry={entry} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

/* ── Page ───────────────────────────────────────────────────────────── */

const FinanclopediaPage = () => {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [search, setSearch] = useState(initialQuery);
  const [category, setCategory] = useState('todos');
  const [difficulty, setDifficulty] = useState('todos');
  const [viewMode, setViewMode] = useState('clusters'); // 'clusters' | 'alpha'
  const [showDropdown, setShowDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Hydrate search from URL ?q= param on navigation
  useEffect(() => {
    const q = searchParams.get('q') || '';
    if (q && q !== search) setSearch(q);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll-aware compact state
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hasActiveFilters = category !== 'todos' || difficulty !== 'todos' || search.length > 0;

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setCategory('todos');
    setDifficulty('todos');
    setShowDropdown(false);
  }, []);

  const featuredEntries = useMemo(
    () => financlopediaEntries.filter((e) => FEATURED_SLUGS.includes(e.slug)),
    []
  );

  const filtered = useMemo(() => {
    return financlopediaEntries.filter((entry) => {
      const matchSearch =
        !search ||
        entry.term.toLowerCase().includes(search.toLowerCase()) ||
        entry.termFull.toLowerCase().includes(search.toLowerCase()) ||
        entry.atomicAnswer.toLowerCase().includes(search.toLowerCase());
      const matchCategory = category === 'todos' || entry.category === category;
      const matchDifficulty =
        difficulty === 'todos' || entry.difficulty === difficulty;
      return matchSearch && matchCategory && matchDifficulty;
    });
  }, [search, category, difficulty]);

  // Enhanced autocomplete: terms + smart search extras
  const autocompleteResults = useMemo(() => {
    if (!search || search.length < 2) return [];
    const q = search.toLowerCase();

    const termMatches = financlopediaEntries
      .filter(
        (e) =>
          e.term.toLowerCase().includes(q) ||
          e.termFull.toLowerCase().includes(q) ||
          e.atomicAnswer.toLowerCase().includes(q)
      )
      .slice(0, 4)
      .map((e) => ({
        key: `term-${e.slug}`,
        type: 'term',
        label: e.term,
        sublabel: e.category,
        path: `/financlopedia/${e.slug}`,
      }));

    const extraMatches = SMART_SEARCH_EXTRAS
      .filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          item.keywords.some((kw) => kw.toLowerCase().includes(q))
      )
      .slice(0, 5)
      .map((item) => ({
        key: `${item.type}-${item.path}`,
        type: item.type,
        label: item.label,
        sublabel: SEARCH_TYPE_LABELS[item.type],
        path: item.path,
      }));

    return [...termMatches, ...extraMatches].slice(0, 8);
  }, [search]);

  useEffect(() => {
    setShowDropdown(search.length >= 2 && autocompleteResults.length > 0);
  }, [search, autocompleteResults]);

  const handleDropdownClick = (path) => {
    setShowDropdown(false);
    setSearch('');
    navigate(path);
  };

  const scrollToLetter = (letter) => {
    const el = document.getElementById(`alpha-${letter}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ bgcolor: '#FFFFFF', minHeight: '100vh' }}>
      <SEOHead
        seoKey="financlopedia"
        title="Diccionario Financiero Canadiense en Español"
        description="Más de 50 términos financieros usados en Canadá explicados para latinos recién llegados. TFSA, RRSP, credit score, hipotecas y más."
        canonical="/financlopedia"
        routeKey="financlopedia"
        schema={schema}
        noIndex={i18n.language !== 'es'}
      />

      {/* ── 1. Hero ── */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 100%)',
          borderBottom: '1px solid #EEF0F3',
          pt: { xs: 3, md: 3.5 }, pb: { xs: 2.5, md: 3 },
        }}
      >
        <Container maxWidth="lg">
          <BreadcrumbNav items={[{ label: 'Diccionario Financiero' }]} />
          <Typography
            variant="h3" component="h1"
            sx={{
              fontWeight: 700, color: '#0F1E3A', mt: 1.5, mb: 1,
              lineHeight: 1.15, fontSize: { xs: '24px', md: '36px' },
              letterSpacing: '-0.02em',
            }}
          >
            Diccionario Financiero Canadiense
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '14px', md: '16px' }, color: '#5F6B7A',
              lineHeight: 1.6, maxWidth: '60ch', mb: 1,
            }}
          >
            {financlopediaEntries.length} términos financieros explicados en español para recién llegados.
            Busca por tema, nivel o navega de la A a la Z.
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
            Actualizado marzo 2026 · Metodología transparente · Sin costo para usuarios
          </Typography>
        </Container>
      </Box>

      {/* ── Sticky Search + Filters (2 rows) ── */}
      <Box
        role="search"
        aria-label="Buscar y filtrar términos"
        sx={{
          bgcolor: '#FFFFFF',
          borderBottom: '1px solid #EEF0F3',
          position: 'sticky',
          top: 0,
          zIndex: 50,
          py: { xs: 1.5, md: 2 },
          boxShadow: isScrolled ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
          transition: 'box-shadow 200ms ease',
        }}
      >
        <Container maxWidth="lg">
          {/* ROW 1: Search + Topic filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
            <ClickAwayListener onClickAway={() => setShowDropdown(false)}>
              <Box sx={{ position: 'relative', flex: '0 1 360px', minWidth: 200 }} ref={searchRef}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar término..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => {
                    if (search.length >= 2 && autocompleteResults.length > 0) setShowDropdown(true);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#5F6B7A', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    ...(search && {
                      endAdornment: (
                        <InputAdornment position="end">
                          <CloseIcon
                            onClick={() => { setSearch(''); setShowDropdown(false); }}
                            sx={{ color: '#5F6B7A', fontSize: 14, cursor: 'pointer', '&:hover': { color: '#1A1A1A' } }}
                          />
                        </InputAdornment>
                      ),
                    }),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px', bgcolor: '#F7F9FC', fontSize: '13px', height: 36,
                      '& fieldset': { borderColor: '#E6E9EE' },
                      '&:hover fieldset': { borderColor: '#5F6B7A' },
                      '&.Mui-focused fieldset': { borderColor: '#1E3A5F' },
                    },
                  }}
                />

                {/* Autocomplete dropdown */}
                {showDropdown && autocompleteResults.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      position: 'absolute', top: '100%', left: 0, right: 0, mt: 0.5,
                      borderRadius: '10px', border: '1px solid #E6E9EE',
                      boxShadow: '0 14px 40px rgba(0,0,0,0.08)', overflow: 'hidden', zIndex: 51,
                    }}
                  >
                    {autocompleteResults.map((result) => {
                      const TypeIcon = SEARCH_TYPE_ICONS[result.type] || SearchIcon;
                      return (
                        <Box
                          key={result.key}
                          onClick={() => handleDropdownClick(result.path)}
                          sx={{
                            px: 2.5, py: 1.25, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            '&:hover': { bgcolor: '#F7F9FC' },
                            borderBottom: '1px solid #F7F9FC',
                          }}
                        >
                          <TypeIcon sx={{ color: '#5F6B7A', fontSize: 16, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A' }}>
                              {result.label}
                            </Typography>
                            <Typography sx={{ fontSize: '11px', color: '#5F6B7A' }}>
                              {result.sublabel}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                    <Box
                      onClick={() => setShowDropdown(false)}
                      sx={{
                        px: 2.5, py: 1.25, cursor: 'pointer', textAlign: 'center',
                        borderTop: '1px solid #E6E9EE', '&:hover': { bgcolor: '#F7F9FC' },
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1E3A5F' }}>
                        Ver todos los resultados ({filtered.length})
                      </Typography>
                    </Box>
                  </Paper>
                )}

                {/* No results */}
                {search.length >= 2 && autocompleteResults.length === 0 && showDropdown && (
                  <Paper
                    elevation={0}
                    sx={{
                      position: 'absolute', top: '100%', left: 0, right: 0, mt: 0.5,
                      borderRadius: '10px', border: '1px solid #E6E9EE', p: 2.5, zIndex: 51, textAlign: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', color: '#5F6B7A' }}>
                      No encontramos ese término.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>

            {/* Topic chips (inline with search on desktop) */}
            <Box
              sx={{
                display: 'flex', flexWrap: { xs: 'nowrap', md: 'wrap' },
                gap: 0.5, flex: 1,
                overflowX: { xs: 'auto', md: 'visible' },
                scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
                maskImage: { xs: 'linear-gradient(to right, black 90%, transparent)', md: 'none' },
                WebkitMaskImage: { xs: 'linear-gradient(to right, black 90%, transparent)', md: 'none' },
              }}
            >
              {CATEGORIES.map((cat) => (
                <Chip
                  key={cat.key}
                  label={cat.label}
                  onClick={() => setCategory(cat.key)}
                  size="small"
                  sx={{
                    fontWeight: 500, fontSize: '12px', height: 28, borderRadius: '999px',
                    transition: 'all 150ms ease', flexShrink: 0,
                    ...(category === cat.key
                      ? { bgcolor: '#1E3A5F', color: '#FFFFFF' }
                      : { bgcolor: 'transparent', color: '#1A1A1A', border: '1px solid #E6E9EE' }),
                    '&:hover': { opacity: 0.85 },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* ROW 2: Difficulty + View mode + Clear filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            {/* Difficulty chips */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {DIFFICULTIES.map((d) => (
                <Chip
                  key={d.key}
                  label={d.label}
                  onClick={() => setDifficulty(difficulty === d.key ? 'todos' : d.key)}
                  size="small"
                  sx={{
                    fontWeight: 500, fontSize: '12px', height: 28, borderRadius: '999px',
                    transition: 'all 150ms ease',
                    ...(difficulty === d.key
                      ? { bgcolor: difficultyColor[d.key]?.bg, color: difficultyColor[d.key]?.text, border: 'none' }
                      : { bgcolor: 'transparent', color: '#5F6B7A', border: '1px solid #E6E9EE' }),
                    '&:hover': { opacity: 0.85 },
                  }}
                />
              ))}
            </Box>

            {/* Divider */}
            <Box sx={{ width: '1px', height: 20, bgcolor: '#E6E9EE', mx: 0.5, display: { xs: 'none', md: 'block' } }} />

            {/* View mode toggle */}
            <Box sx={{ display: 'inline-flex', border: '1px solid #E6E9EE', borderRadius: '8px', overflow: 'hidden' }}>
              {[
                { key: 'clusters', label: 'Por categorías' },
                { key: 'alpha', label: 'A–Z' },
              ].map((opt) => (
                <Button
                  key={opt.key}
                  size="small"
                  onClick={() => setViewMode(opt.key)}
                  sx={{
                    textTransform: 'none', fontSize: '12px', fontWeight: 600,
                    px: 1.5, py: 0.5, borderRadius: 0, minWidth: 'auto', height: 28,
                    ...(viewMode === opt.key
                      ? { bgcolor: '#1E3A5F', color: '#FFFFFF', '&:hover': { bgcolor: '#1A3152' } }
                      : { bgcolor: '#FFFFFF', color: '#5F6B7A', '&:hover': { bgcolor: '#F7F9FC' } }),
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </Box>

            {/* Counter + clear */}
            {hasActiveFilters && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                <Typography sx={{ fontSize: '12px', color: '#5F6B7A', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
                </Typography>
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<CloseIcon sx={{ fontSize: '12px !important' }} />}
                  sx={{
                    textTransform: 'none', fontSize: '11px', color: '#5F6B7A', whiteSpace: 'nowrap',
                    minWidth: 'auto', px: 1,
                    '&:hover': { color: '#1A1A1A', bgcolor: '#F7F9FC' },
                  }}
                >
                  Limpiar
                </Button>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* ── 2. Content ── */}
      <Box component="section" sx={{ bgcolor: '#FFFFFF', pt: { xs: 3, md: 4 }, pb: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">

          {/* Filtered view */}
          {hasActiveFilters ? (
            <>
              {filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#F7F9FC', borderRadius: '14px' }}>
                  <Typography sx={{ fontSize: '15px', color: '#5F6B7A' }}>
                    No se encontraron términos con esos filtros.
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleClearFilters}
                    sx={{
                      mt: 1.5, textTransform: 'none', fontSize: '13px', color: '#1E3A5F', fontWeight: 600,
                      '&:hover': { bgcolor: 'rgba(30,58,95,0.06)' },
                    }}
                  >
                    Limpiar filtros y ver todos
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2.5}>
                  {filtered.map((entry) => (
                    <Grid item xs={12} sm={6} md={4} key={entry.slug}>
                      <EntryCard entry={entry} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </>

          ) : viewMode === 'clusters' ? (
            /* ── Cluster view ── */
            <>
              {/* Featured strip */}
              <Box sx={{ mb: 5 }}>
                <Typography
                  sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2 }}
                >
                  Términos destacados
                </Typography>
                <Grid container spacing={2.5}>
                  {featuredEntries.map((entry) => (
                    <Grid item xs={12} sm={6} md={4} key={entry.slug}>
                      <EntryCard entry={entry} featured />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {CLUSTERS.map((cluster) => (
                <ClusterSection key={cluster.id} cluster={cluster} />
              ))}
            </>

          ) : (
            /* ── Alphabetical A–Z view ── */
            <>
              {/* A–Z jump bar */}
              <Box
                sx={{
                  display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3,
                  px: 1, py: 1, bgcolor: '#F7F9FC', borderRadius: '10px',
                  border: '1px solid #EEF0F3',
                }}
              >
                {AVAILABLE_LETTERS.map((letter) => (
                  <Button
                    key={letter}
                    size="small"
                    onClick={() => scrollToLetter(letter)}
                    sx={{
                      minWidth: 32, height: 32, p: 0, fontSize: '13px', fontWeight: 700,
                      color: '#1E3A5F', borderRadius: '6px',
                      '&:hover': { bgcolor: '#1E3A5F', color: '#FFFFFF' },
                    }}
                  >
                    {letter}
                  </Button>
                ))}
              </Box>

              {/* Letter groups */}
              {ALPHA_GROUPS.map(([letter, entries]) => (
                <Box key={letter} id={`alpha-${letter}`} sx={{ mb: 3, scrollMarginTop: '120px' }}>
                  <Typography
                    sx={{
                      fontSize: '20px', fontWeight: 700, color: '#1E3A5F',
                      borderBottom: '2px solid #1E3A5F', display: 'inline-block',
                      pb: 0.25, mb: 1,
                    }}
                  >
                    {letter}
                  </Typography>
                  <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid #EEF0F3', borderRadius: '10px', overflow: 'hidden' }}>
                    {entries.map((entry) => (
                      <AlphaListItem key={entry.slug} entry={entry} />
                    ))}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Container>
      </Box>

      {/* ── Trust Strip ── */}
      <TrustStrip />

      {/* ── Compact Newsletter ── */}
      <Box component="section" sx={{ bgcolor: '#FFFFFF', borderTop: '1px solid #EEF0F3', py: { xs: 2.5, md: 3 } }}>
        <Container maxWidth="lg">
          <Box
            component="form"
            action="https://buttondown.com/api/emails/embed-subscribe/Finmaple"
            method="post"
            onSubmit={() => {
              if (window.plausible) window.plausible('Newsletter Submit', { props: { source: 'financlopedia-compact' } });
              gtmNewsletterSubmit({ source: 'financlopedia-compact' });
            }}
            sx={{
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center', gap: { xs: 1.5, sm: 2 },
              maxWidth: 580, mx: 'auto',
            }}
          >
            <EmailOutlined sx={{ fontSize: 18, color: '#1E3A5F', display: { xs: 'none', sm: 'block' } }} />
            <Typography sx={{ fontSize: '13px', color: '#5F6B7A', fontWeight: 500 }}>
              Recibe nuevos términos y guías cada semana.
            </Typography>
            <TextField
              name="email" type="email" required size="small" placeholder="tu@email.com"
              sx={{
                flex: 1, minWidth: 160,
                '& .MuiOutlinedInput-root': {
                  height: 36, borderRadius: '8px', fontSize: '13px',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#1E3A5F' },
                  '&.Mui-focused fieldset': { borderColor: '#1E3A5F' },
                },
              }}
            />
            <Button
              type="submit" variant="contained" size="small"
              sx={{
                height: 36, px: 2.5, fontSize: '13px', fontWeight: 700,
                borderRadius: '8px', bgcolor: '#1E3A5F', color: '#fff',
                textTransform: 'none', whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#1A3152' },
              }}
            >
              Suscribirme
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FinanclopediaPage;
